// The Go backend: it lowers the monomorphized Core IR (see middle-end/core.ts) to
// Go. Because Core is already fully resolved — a concrete type on every node,
// dispatch explicit as staticCall/dynCall, operators and trait defaults lowered
// away, generics instantiated into ordinary concrete CoreFuncs/CoreStructs — this
// backend is a *printer*: it decides physical representation (`*Object` boxing,
// value types, the STM cell format, the mvcc/in-place runtime) but never types,
// dispatch, or specialization. That separation is the point of Core: a second
// backend (C, wasm) would be another printer like this one.
//
// It covers the language — structs (heap + value), enums and
// `match`, operators, transactions (`txn`/`go`) under both memory modes (the
// LockPlan's mvcc-vs-in-place / occ-vs-2PL decision is baked into Core and read off
// it here), Set/Dict operations and iteration, dynamic dispatch (a `dynCall` calls
// the `Trait_method` stub built from Core's resolved dispatch tables), the
// Iterable/Iterator protocol, generics (functions, structs, methods — all
// monomorphized upstream), and first-class functions (`funcRef` / `closure` /
// `callValue`).
//
// This file lowers the *language*. The native `builtin fn`s (print/str/math,
// file IO, TCP net) — the backend's platform surface — live in `builtins.ts`,
// reached from the `builtin` case through a narrow context; the hand-written Go
// runtimes live in `runtime.ts`.

import * as ast from "../../frontend/ast.js";
import { analyze } from "../../frontend/checker.js";
import { Type, UNKNOWN, VOID } from "../../frontend/types.js";
import {
  CoreDispatch,
  CoreExpr,
  CoreFunc,
  CoreLValue,
  CoreMemoryMode,
  CoreProgram,
  CoreStmt,
  CoreStruct,
  IterAdapter,
  IterDriver,
} from "../../middle-end/core.js";
import { Elaboration } from "../../middle-end/elaboration.js";
import { planLocks } from "../../middle-end/lockplan.js";
import { monomorphize } from "../../middle-end/monomorphize.js";
import { eliminateDeadCode } from "../../middle-end/dce.js";
import { emitBuiltin } from "./builtins.js";
import { GoGenError } from "./errors.js";
import { RUNTIME_HANDLES, RUNTIME_INPLACE, RUNTIME_LINEREADER, RUNTIME_MVCC } from "./runtime.js";

// The Go backend's fallback error. Defined in `./errors.js` (a leaf module, so
// `builtins.ts` can throw it too without a cycle) and re-exported here, which is
// where callers import it from.
export { GoGenError };

export interface GoGenOptions {
  /** "occ" (default): every txn optimistic, mvcc memory. "hybrid": lock-infer a
   *  static footprint and use in-place memory where sound. */
  strategy?: "occ" | "hybrid";
  /** After `main` finishes, dump each heap-object local's final value to stderr —
   *  a verification hook for concurrency tests (the program itself may print nothing). */
  dump?: boolean;
}

const GO_KEYWORDS = new Set([
  "break", "case", "chan", "const", "continue", "default", "defer", "else",
  "fallthrough", "for", "func", "go", "goto", "if", "import", "interface",
  "map", "package", "range", "return", "select", "struct", "switch", "type", "var",
]);
function ident(name: string): string {
  return GO_KEYWORDS.has(name) ? name + "_" : name;
}

/** The runtime-record display tag for each built-in container. All three containers
 *  share one `collRec` shape; distinct tags let display and dispatch tell them apart. */
const CONTAINER_REC: Record<"Set" | "Dict" | "List", string> = {
  Set: "setRec",
  Dict: "dictRec",
  List: "listRec",
};

/** Lower a program to Go. `elab` is the checker's Elaboration (the shared IR); when
 *  omitted it is computed here. The LockPlan's memory-mode and per-txn strategy
 *  decisions are baked into Core (see monomorphize) and read off it by this printer.
 *  Throws `GoGenError` for a construct the backend can't lower yet. */
export function generateGo(program: ast.Program, opts: GoGenOptions = {}, elab?: Elaboration): string {
  const elaboration = elab ?? analyze(program).elaboration;
  const plan = planLocks(program, { strategy: opts.strategy ?? "occ", elaboration });
  return new GoGen(eliminateDeadCode(monomorphize(program, plan, elaboration)), opts).emit();
}

class GoGen {
  /** struct name -> is it a value type (by-value) vs a heap `*Object`. */
  private valueType = new Map<string, boolean>();
  /** heap struct names, in declaration order, for `*Object` display tags. */
  private heapStructs: string[] = [];
  /** types implementing `Show` (a `Type_show` function exists). */
  private showImpls = new Set<string>();
  private uses = { math: false, rand: false, net: false, fileHandle: false, ioPkg: false, readLine: false };
  /** The program's memory mode, baked into Core by the LockPlan. Governs record
   *  layout (`__ver` header), field reads (`get(tx)` vs `rec`), field writes
   *  (copy-on-write vs in-place), and which STM runtime is emitted. */
  private readonly memory: CoreMemoryMode;

  /** Every function by id, for a dispatch case to read its target's param types. */
  private funcById = new Map<string, CoreFunc>();

  /** The return type of the function (or closure) currently being emitted — the type
   *  of the capture variable a `return` inside its `txn` writes to. */
  private currentRet: Type = VOID;
  /** When inside a `txn` whose body has `return`s, where to route them: the capture
   *  variable and (in the conditional case) the "did we return?" flag. A `return`
   *  can't leave Go's `Run` closure directly, so it assigns here and bare-returns;
   *  the code after `Run` propagates it. Null outside such a `txn`, and reset to null
   *  at every closure boundary (a lambda's `return` is its own). */
  private txnRet: { ret: string; flag: string | null } | null = null;
  private txnCounter = 0;

  constructor(private prog: CoreProgram, private opts: GoGenOptions = {}) {
    this.memory = prog.memory;
    for (const s of prog.structs) {
      this.valueType.set(s.name, s.valueType);
      if (!s.valueType) this.heapStructs.push(s.name);
    }
    for (const f of prog.funcs) this.funcById.set(f.id, f);
    for (const f of prog.funcs) {
      const m = /^(.+)_show$/.exec(f.id);
      if (m && f.self) this.showImpls.add(m[1]!);
    }
  }

  emit(): string {
    const decls = this.emitTypeDecls();
    const show = this.emitShow();
    const funcs = this.prog.funcs.map((f) => this.emitFunc(f)).join("\n\n");
    const iterators = this.emitIterAdapters();
    const dispatch = this.prog.dispatch.map((d) => this.emitDispatchFn(d)).join("\n\n");
    const main = this.emitMain();
    // Imports and the handle runtime read `uses`, populated while emitting the
    // funcs above — so both are computed after them.
    const imports = this.emitImports();
    // The shared handle registry backs both sockets and open files; the line-reader
    // runtime (buffered readers, keyed by handle) is emitted only for `readLine`.
    const handleRuntime = this.uses.net || this.uses.fileHandle ? RUNTIME_HANDLES : "";
    const lineReaderRuntime = this.uses.readLine ? RUNTIME_LINEREADER : "";
    const inPlace = this.memory === "in-place";
    const banner = inPlace
      ? "// memory: in-place (mutable records under per-object locks; no versioning)"
      : "// memory: mvcc (versioned records; lock-free reads; OCC + 2PL)";
    return [
      "// Code generated by stm-lang gogen. DO NOT EDIT.",
      banner,
      "package main",
      "",
      imports,
      "",
      inPlace ? RUNTIME_INPLACE : RUNTIME_MVCC,
      "",
      handleRuntime,
      "",
      lineReaderRuntime,
      "",
      decls,
      "",
      show,
      "",
      funcs,
      "",
      iterators,
      "",
      dispatch,
      "",
      main,
      "",
    ].join("\n");
  }

  private emitImports(): string {
    const list = ["fmt", "os", "runtime", "sync", "sync/atomic", "time", "unsafe"];
    if (this.uses.math) list.push("math");
    if (this.uses.rand) list.push("math/rand");
    if (this.uses.net) list.push("net");
    if (this.uses.ioPkg) list.push("io");
    if (this.uses.readLine) list.push("bufio");
    list.sort();
    return "import (\n" + list.map((p) => `\t"${p}"`).join("\n") + "\n)";
  }

  private tag(name: string): string {
    return `tag_${name}`;
  }

  // -- type declarations --------------------------------------------------

  private emitTypeDecls(): string {
    const out: string[] = [];
    const tagNames = ["setRec", "dictRec", "listRec", ...this.heapStructs];
    out.push(`const (\n${tagNames.map((n, i) => `\t${this.tag(n)} uint16 = ${i + 1}`).join("\n")}\n)`);
    for (const s of this.prog.structs) {
      const fields = s.fields.map((f) => `\t${ident(f.name)} ${this.goType(f.type)}`).join("\n");
      // In mvcc, a heap record carries `__ver` (first field, aliases recHdr); a
      // value type — and every record in in-place mode (no versioning) — carries none.
      const hdr = s.valueType || this.memory === "in-place" ? "" : "\t__ver uint64\n";
      out.push(`type ${s.name} struct {\n${hdr}${fields}\n}`);
    }
    for (const e of this.prog.enums) {
      out.push(`type ${e.name} interface { is${e.name}() }`);
      for (const v of e.variants) {
        const fields = v.fields.map((f) => `\t${ident(f.name)} ${this.goType(f.type)}`).join("\n");
        out.push(`type ${e.name}_${v.name} struct {${fields ? `\n${fields}\n` : ""}}`);
        out.push(`func (${e.name}_${v.name}) is${e.name}() {}`);
      }
    }
    return out.join("\n\n");
  }

  // -- __show / __showRec -------------------------------------------------

  private emitShow(): string {
    const enumCases: string[] = [];
    for (const e of this.prog.enums) {
      const showable = this.showImpls.has(e.name);
      for (const v of e.variants) {
        const parts = v.fields.map((f) => `"${f.name}: " + __show(x.${ident(f.name)})`).join(` + ", " + `);
        const body = showable
          ? `return ${e.name}_show(nil, x)`
          : v.fields.length
            ? `return "${v.name}(" + ${parts} + ")"`
            : `return "${v.name}"`;
        enumCases.push(`\tcase ${e.name}_${v.name}:\n\t\t${body}`);
      }
    }
    const valueCases: string[] = [];
    const recCases: string[] = [];
    const showObjCases: string[] = [];
    for (const s of this.prog.structs) {
      if (this.showImpls.has(s.name)) {
        if (s.valueType) valueCases.push(`\tcase ${s.name}:\n\t\treturn ${s.name}_show(nil, x)`);
        else showObjCases.push(`\t\tcase ${this.tag(s.name)}:\n\t\t\treturn ${s.name}_show(nil, x)`);
        continue;
      }
      const recv = s.valueType ? "x" : "r";
      const parts = s.fields.map((f) => `"${f.name}: " + __show(${recv}.${ident(f.name)})`).join(` + ", " + `);
      if (s.valueType) {
        valueCases.push(`\tcase ${s.name}:\n\t\t${s.fields.length ? `return "${s.name}{" + ${parts} + "}"` : `return "${s.name}{}"`}`);
      } else {
        const body = s.fields.length ? `r := (*${s.name})(p); return "${s.name}{" + ${parts} + "}"` : `return "${s.name}{}"`;
        recCases.push(`\tcase ${this.tag(s.name)}:\n\t\t${body}`);
      }
    }
    // A container with a user `Show` impl (`impl<T> Show for Set<T>`) is a tagged
    // `*Object` too — its `Set_show`/`Dict_show`/`List_show` slots into the same tag
    // switch, taking precedence over the default `Set(N)` in __showRec.
    for (const container of ["Set", "Dict", "List"] as const) {
      if (this.showImpls.has(container))
        showObjCases.push(`\t\tcase ${this.tag(CONTAINER_REC[container])}:\n\t\t\treturn ${container}_show(nil, x)`);
    }
    const rec = this.memory === "in-place" ? "x.rec" : "x.get(nil)";
    const objArm = showObjCases.length
      ? `\tcase *Object:\n\t\tswitch x.tag {\n${showObjCases.join("\n")}\n\t\t}\n\t\treturn __showRec(x.tag, ${rec})\n`
      : `\tcase *Object:\n\t\treturn __showRec(x.tag, ${rec})\n`;
    return (
      `func __show(v any) string {\n\tswitch x := v.(type) {\n` +
      `\tcase nil:\n\t\treturn "()"\n` +
      `\tcase int64:\n\t\treturn fmt.Sprintf("%d", x)\n` +
      `\tcase float64:\n\t\tif x == float64(int64(x)) { return fmt.Sprintf("%.1f", x) }\n\t\treturn fmt.Sprintf("%g", x)\n` +
      `\tcase bool:\n\t\treturn fmt.Sprintf("%t", x)\n` +
      `\tcase string:\n\t\treturn x\n` +
      // A char (`rune` = int32) renders as its glyph, so `str('x')` is `"x"`.
      `\tcase rune:\n\t\treturn string(x)\n` +
      objArm +
      (valueCases.length ? valueCases.join("\n") + "\n" : "") +
      (enumCases.length ? enumCases.join("\n") + "\n" : "") +
      `\tdefault:\n\t\treturn fmt.Sprintf("%v", x)\n\t}\n}\n\n` +
      `func __showRec(tag uint16, p unsafe.Pointer) string {\n\tswitch tag {\n` +
      // Collections only exist in mvcc mode (a collection field forces it), so their
      // record helpers (collRec/__collRecLen) are only in RUNTIME_MVCC — guard on it.
      (this.memory === "in-place"
        ? ""
        : `\tcase ${this.tag("setRec")}:\n\t\treturn fmt.Sprintf("Set(%d)", __collRecLen((*collRec)(p)))\n` +
          `\tcase ${this.tag("dictRec")}:\n\t\treturn fmt.Sprintf("Dict(%d)", __collRecLen((*collRec)(p)))\n` +
          `\tcase ${this.tag("listRec")}:\n\t\treturn fmt.Sprintf("List(%d)", __collRecLen((*collRec)(p)))\n`) +
      (recCases.length ? recCases.join("\n") + "\n" : "") +
      `\t}\n\treturn "?"\n}`
    );
  }

  // -- functions ----------------------------------------------------------

  private emitFunc(fn: CoreFunc): string {
    const env = new Env();
    const params = ["tx *Txn"];
    if (fn.self) {
      env.declare("self", fn.self.type);
      params.push(`self ${this.goType(fn.self.type)}`);
    }
    for (const p of fn.params) {
      env.declare(p.name, p.type);
      params.push(`${ident(p.name)} ${this.goType(p.type)}`);
    }
    const retStr = fn.ret.k === "void" ? "" : " " + this.goType(fn.ret);
    // The function's return type is the target for any `return` captured out of a
    // `txn` in its body (see emitTxn). Top-level funcs are emitted one at a time, so a
    // plain field assignment (no save/restore) is enough.
    this.currentRet = fn.ret;
    this.txnRet = null;
    let body = fn.body.map((s) => this.emitStmt(s, env, 1)).join("");
    const park =
      (fn.self ? "\t_ = self\n" : "") + fn.params.map((p) => `\t_ = ${ident(p.name)}\n`).join("");
    // The user's `main` becomes `main_stm`; the real entry point is emitMain().
    const goName = fn.id === "main" ? "main_stm" : ident(fn.id);
    if (fn.id === "main") {
      body += `\twg.Wait()\n`; // wait for spawned threads (their locals still in scope)
      if (this.opts.dump) {
        // Dump each heap-object local's final value — a verification hook for
        // concurrency tests, whose programs may not print their end state themselves.
        for (const name of this.mainLocals(fn)) {
          body += `\tif __o, __ok := any(${ident(name)}).(*Object); __ok { fmt.Fprintln(os.Stderr, "${name} =", __show(__o)) }\n`;
        }
      }
    }
    return `func ${goName}(${params.join(", ")})${retStr} {\n${park}${body}}`;
  }

  /** The names bound at `main`'s top level (its `let`s and local re-bindings), in
   *  order — the candidates the `dump` hook renders. */
  private mainLocals(fn: CoreFunc): string[] {
    const names: string[] = [];
    for (const s of fn.body) {
      if (s.s === "let") names.push(s.name);
      else if (s.s === "assign" && s.target.lv === "local") names.push(s.target.name);
    }
    return [...new Set(names)];
  }

  private emitStmt(stmt: CoreStmt, env: Env, depth: number): string {
    const pad = "\t".repeat(depth);
    switch (stmt.s) {
      case "let": {
        env.declare(stmt.name, stmt.type);
        const value = this.coerce(this.emitExpr(stmt.value, env), stmt.value.type, stmt.type);
        // An enum value is often a concrete variant literal (`Option__int_Some{…}`),
        // which `:=` would bind at the variant type, not the enum interface — so a
        // later `match` on it fails to type-switch. Pin the interface type with `var`.
        const decl =
          stmt.type.k === "enum" ? `var ${ident(stmt.name)} ${this.goType(stmt.type)} = ${value}` : `${ident(stmt.name)} := ${value}`;
        return `${pad}${decl}\n${pad}_ = ${ident(stmt.name)}\n`;
      }
      case "assign": {
        const t = stmt.target.type;
        const rhs = this.coerce(this.emitExpr(stmt.value, env), stmt.value.type, t);
        if (stmt.target.lv === "local") {
          return `${pad}${ident(stmt.target.name)} = ${rhs}\n`;
        }
        return this.emitFieldAssign(stmt.target, rhs, env, pad);
      }
      case "expr": {
        const code = this.emitExpr(stmt.expr, env);
        return stmt.expr.type.k === "void" ? `${pad}${code}\n` : `${pad}_ = ${code}\n`;
      }
      case "return": {
        // `return f()` where `f` returns nothing is legal STM (an expression-bodied
        // lambda over a void call lowers to exactly this) but not legal Go, which
        // rejects a no-value call in a `return`. Emit the call as a statement and
        // return bare — the same thing, minus the value that was never there.
        const voidValue = !!stmt.value && stmt.value.type.k === "void";
        const before = voidValue ? `${pad}${this.emitExpr(stmt.value!, env)}\n` : "";
        if (this.txnRet) {
          // Inside a `txn`: a Go `return` would leave the `Run` closure, not the
          // enclosing function. Assign the value to the capture var and bare-return
          // from the closure; the code after `Run` performs the real function return.
          const set = stmt.value && !voidValue ? `${pad}${this.txnRet.ret} = ${this.emitExpr(stmt.value, env)}\n` : "";
          const flag = this.txnRet.flag ? `${pad}${this.txnRet.flag} = true\n` : "";
          return `${before}${set}${flag}${pad}return\n`;
        }
        if (voidValue) return `${before}${pad}return\n`;
        return stmt.value ? `${pad}return ${this.emitExpr(stmt.value, env)}\n` : `${pad}return\n`;
      }
      case "if": {
        const cond = this.emitExpr(stmt.cond, env);
        let out = `${pad}if ${cond} {\n${this.emitBlock(stmt.then, env, depth + 1)}${pad}}`;
        if (stmt.else) out += ` else {\n${this.emitBlock(stmt.else, env, depth + 1)}${pad}}`;
        return out + "\n";
      }
      case "while":
        return `${pad}for ${this.emitExpr(stmt.cond, env)} {\n${this.emitBlock(stmt.body, env, depth + 1)}${pad}}\n`;
      case "for":
        return this.emitFor(stmt, env, depth);
      case "match":
        return this.emitMatch(stmt, env, depth);
      case "block":
        return `${pad}{\n${this.emitBlock(stmt.body, env, depth + 1)}${pad}}\n`;
      case "go":
        return this.emitGo(stmt, env, pad);
      case "txn":
        return this.emitTxn(stmt, env, depth);
      case "retry":
        // Unwind to the enclosing `Run` by panicking a distinguished sentinel; the
        // OCC loop catches it, parks on the attempt's read set, and re-runs on a
        // committed overlap (see `Run`/`retryPark` in runtime.ts). `panic` is a Go
        // terminating statement, so a `retry` at the tail of a value-returning
        // match arm still satisfies the compiler's "missing return" analysis.
        return `${pad}panic(__retryToken)\n`;
    }
  }

  /** A field write. In-place mutates the record under the enclosing 2PL lock; mvcc
   *  copies the record, mutates the copy, and buffers it (copy-on-write). Core has
   *  no compound assignment, so the RHS is already the fully-resolved value. */
  private emitFieldAssign(target: Extract<CoreLValue, { lv: "field" }>, rhs: string, env: Env, pad: string): string {
    const ot = target.object.type;
    if (ot.k !== "struct") throw new GoGenError(`field assignment on ${ot.k} not lowered yet`);
    if (this.valueType.get(ot.name)) throw new GoGenError(`cannot assign to field of value type \`${ot.name}\``);
    const obj = this.emitExpr(target.object, env);
    const field = ident(target.field);
    if (this.memory === "in-place") {
      return `${pad}(*${ot.name})(${obj}.rec).${field} = ${rhs}\n`;
    }
    return (
      `${pad}{\n` +
      `${pad}\t__r := *(*${ot.name})(${obj}.get(tx))\n` +
      `${pad}\t__r.${field} = ${rhs}\n` +
      `${pad}\t${obj}.set(tx, unsafe.Pointer(&__r))\n` +
      `${pad}}\n`
    );
  }

  private emitFor(stmt: Extract<CoreStmt, { s: "for" }>, env: Env, depth: number): string {
    const pad = "\t".repeat(depth);
    const inner = env.child();
    inner.declare(stmt.varName, stmt.varType);
    const v = ident(stmt.varName);
    const iter = stmt.iter;
    if (iter.it === "collection" && (iter.container === "Set" || iter.container === "List")) {
      // A Set yields its elements; a List yields its elements in index order. Both
      // hand back a `[]any` (`__collKeys` / `__listValues`), so bind each back to the
      // element's concrete Go type (see `fromAny`).
      const coll = this.emitExpr(iter.value, inner);
      const snap = iter.container === "Set" ? "__collKeys" : "__listValues";
      return (
        `${pad}for _, __e := range ${snap}(tx, ${coll}) {\n` +
        `${pad}\t${v} := ${this.fromAny("__e", stmt.varType)}\n` +
        `${pad}\t_ = ${v}\n${this.emitBlock(stmt.body, inner, depth + 1)}${pad}}\n`
      );
    }
    if (iter.it === "collection" && iter.container === "Dict") {
      // A Dict yields a `Pair<K, V>` per entry (`first` = key, `second` = value),
      // materialized from the key set: for each key, read its value and build the
      // (monomorphized, concrete) Pair struct — an ordinary heap `*Object`.
      const coll = this.emitExpr(iter.value, inner);
      const pair = this.prog.structs.find((s) => stmt.varType.k === "struct" && s.name === stmt.varType.name)!;
      const [kf, vf] = pair.fields; // first: K, second: V
      const key = this.fromAny("__k", kf!.type);
      const val = this.fromAny(`__dictGet(tx, ${coll}, __k)`, vf!.type);
      const build = `newObject(${this.tag(pair.name)}, unsafe.Pointer(&${pair.name}{${ident(kf!.name)}: ${key}, ${ident(vf!.name)}: ${val}}))`;
      return (
        `${pad}for _, __k := range __collKeys(tx, ${coll}) {\n` +
        `${pad}\t${v} := ${build}\n` +
        `${pad}\t_ = ${v}\n${this.emitBlock(stmt.body, inner, depth + 1)}${pad}}\n`
      );
    }
    if (iter.it === "iterator" || iter.it === "iterable") {
      // The Iterator protocol: an Iterable yields a fresh iterator via iter(); an
      // Iterator is its own. Then pull next() (a monomorphized `Option<E>`, named by
      // `iter.option`) until it returns None, binding `Some.value` — already the
      // concrete element type — to the loop variable. The drivers are the concrete
      // functions monomorphize resolved.
      const value = this.emitExpr(iter.value, inner);
      const itInit = iter.it === "iterable" ? this.emitDriver(iter.iter, value) : value;
      const body = this.emitBlock(stmt.body, inner, depth + 2);
      return (
        `${pad}{\n` +
        `${pad}\t__it := ${itInit}\n` +
        `${pad}\t_ = __it\n` +
        `${pad}\tfor {\n` +
        `${pad}\t\t__opt := ${this.emitDriver(iter.next, "__it")}\n` +
        `${pad}\t\t__some, __ok := __opt.(${iter.option}_Some)\n` +
        `${pad}\t\tif !__ok {\n${pad}\t\t\tbreak\n${pad}\t\t}\n` +
        `${pad}\t\t${v} := __some.value\n` +
        `${pad}\t\t_ = ${v}\n` +
        body +
        `${pad}\t}\n` +
        `${pad}}\n`
      );
    }
    throw new GoGenError(`iteration (${iter.it}${iter.it === "collection" ? ` ${iter.container}` : ""}) not lowered from Core yet`);
  }

  /** A resolved iterator driver — `iter()` or `next()` — as a call. A static driver
   *  is a direct call to the concrete function; a dynamic one goes through the trait
   *  dispatch stub (which must have been built for that `(trait, method)`). */
  private emitDriver(d: IterDriver, arg: string): string {
    return d.via === "static"
      ? `${ident(d.target)}(tx, ${arg})`
      : `${d.trait}_${ident(d.method)}(tx, ${arg})`;
  }

  private emitGo(stmt: Extract<CoreStmt, { s: "go" }>, env: Env, pad: string): string {
    const args = stmt.args.map((a) => ", " + this.emitExpr(a, env)).join("");
    // A spawned goroutine starts a fresh top-level context: no ambient txn (nil).
    return `${pad}spawn(func() { ${ident(stmt.target)}(nil${args}) })\n`;
  }

  private emitTxn(stmt: Extract<CoreStmt, { s: "txn" }>, env: Env, depth: number): string {
    const pad = "\t".repeat(depth);
    // A `return` inside the `txn` returns from the *enclosing function*, but the txn
    // body is a `func(tx *Txn){…}` closure handed to `Run`, so a Go `return` there
    // would only leave the closure. Capture the value into a var and route the real
    // return to after `Run` (see the "return" case in emitStmt).
    const prevTxnRet = this.txnRet;
    let decls = "";
    let post = "";
    if (stmtsHaveReturn(stmt.body)) {
      const n = this.txnCounter++;
      const retVar = `__txnRet${n}`;
      const flagVar = `__txnRet${n}Ok`;
      const rt = this.currentRet;
      const always = stmtsAlwaysExit(stmt.body); // every path returns/retries → no fall-through
      if (rt.k !== "void") decls += `${pad}var ${retVar} ${this.goType(rt)}\n`;
      if (!always) decls += `${pad}var ${flagVar} bool\n`;
      this.txnRet = { ret: retVar, flag: always ? null : flagVar };
      // Propagate the captured return one level out: into an enclosing txn's capture
      // (bare-return again), or as the function's real `return`.
      const propagate = (ind: string): string => {
        if (prevTxnRet) {
          const s = rt.k === "void" ? "" : `${ind}${prevTxnRet.ret} = ${retVar}\n`;
          const f = prevTxnRet.flag ? `${ind}${prevTxnRet.flag} = true\n` : "";
          return `${s}${f}${ind}return\n`;
        }
        return rt.k === "void" ? `${ind}return\n` : `${ind}return ${retVar}\n`;
      };
      post = always ? propagate(pad) : `${pad}if ${flagVar} {\n${propagate(pad + "\t")}${pad}}\n`;
    }
    const body = this.emitBlock(stmt.body, env, depth + 1);
    this.txnRet = prevTxnRet;
    const closure = `func(tx *Txn) {\n${body}${pad}}`;
    let call: string;
    if (stmt.strategy.kind === "occ") {
      // Optimistic: snapshot, buffer writes, validate + retry. Only sound in mvcc,
      // where the LockPlan places it (in-place programs are all-2PL by definition).
      call = `${pad}Run(tx, ${closure})\n`;
    } else {
      // Pessimistic 2PL over a statically-known footprint of root locals.
      const roots = stmt.strategy.footprint.map((n) => ident(n));
      const note = `${pad}// lock-inferred footprint: {${stmt.strategy.footprint.join(", ")}}\n`;
      let c: string;
      if (roots.length === 0) c = `RunLockedN(tx, nil, ${closure})`;
      else if (roots.length === 1) c = `RunLocked1(tx, ${roots[0]}, ${closure})`;
      else if (roots.length === 2) c = `RunLocked2(tx, ${roots[0]}, ${roots[1]}, ${closure})`;
      else c = `RunLockedN(tx, []*Object{${roots.join(", ")}}, ${closure})`;
      call = `${note}${pad}${c}\n`;
    }
    return decls + call + post;
  }

  // -- built-in collection iterators --------------------------------------

  /** Native `iter`/`next` for each built-in collection cursor a container's `iter()`
   *  hands out (`SetIter<int>` / `DictIter<K, V>`; see monomorphize's
   *  `containerAdapter`). The cursor holds only its collection (`coll`, an STM
   *  field); its walk state — a snapshot of the entries and a position — lives in a
   *  side table keyed by the cursor's identity, outside the versioned store, exactly
   *  like the interpreter's transient cursor. `iter()` hands back a fresh cursor over
   *  the same collection (re-iterable); `next()` snapshots on first use, then walks,
   *  yielding a concretely typed `Option` (this is what the erased dynamic dispatch
   *  couldn't do — here the element types are known). */
  private emitIterAdapters(): string {
    if (!this.prog.iterAdapters.length) return "";
    const state =
      "// Transient walk state for a built-in collection cursor, keyed by the cursor\n" +
      "// object's identity (a cursor holds only its collection). Outside the versioned\n" +
      "// store: iterating a collection is not itself an STM operation.\n" +
      "type __iterState struct {\n\tkeys []any\n\tpos  int\n\tcoll *Object\n}\n\n" +
      "var __iterStates sync.Map // map[*Object]*__iterState";
    return [state, ...this.prog.iterAdapters.map((a) => this.emitIterAdapter(a))].join("\n\n");
  }

  private emitIterAdapter(a: IterAdapter): string {
    const rec = this.memory === "in-place" ? "self.rec" : "self.get(tx)";
    const coll = `(*${a.struct})(${rec}).coll`;
    // The container's `iter`: build a fresh cursor holding the collection. Installed
    // on the collection at construction and called by the Iterable dispatch and a
    // direct `container.iter()`, so a set/dict is iterated *as itself*.
    const iterFn =
      `func ${a.iterFn}(tx *Txn, coll *Object) any {\n` +
      `\treturn newObject(${this.tag(a.struct)}, unsafe.Pointer(&${a.struct}{coll: coll}))\n}`;
    // The Some payload: a Set/List yields its narrowed element (a Set's membership
    // key, a List's stored value); a Dict builds a `Pair` from each key and its
    // looked-up value.
    let yieldStmt: string;
    if (a.container === "Set" || a.container === "List") {
      yieldStmt =
        `\t__v := st.keys[st.pos]\n\tst.pos++\n` +
        `\treturn ${a.option}_Some{value: ${this.fromAny("__v", a.elem)}}`;
    } else {
      const key = this.fromAny("__k", a.elem);
      const val = this.fromAny("__dictGet(tx, st.coll, __k)", a.value!);
      yieldStmt =
        `\t__k := st.keys[st.pos]\n\tst.pos++\n` +
        `\t__p := newObject(${this.tag(a.pair!)}, unsafe.Pointer(&${a.pair}{first: ${key}, second: ${val}}))\n` +
        `\treturn ${a.option}_Some{value: __p}`;
    }
    // The snapshot source: a Set's members / a Dict's keys via `__collKeys`; a List's
    // elements in index order via `__listValues`.
    const snapKeys = a.container === "List" ? `__listValues(tx, ${coll})` : `__collKeys(tx, ${coll})`;
    const snap = `&__iterState{keys: ${snapKeys}${a.container === "Dict" ? `, coll: ${coll}` : ""}}`;
    const nextFn =
      `func ${a.struct}_next(tx *Txn, self *Object) any {\n` +
      `\tvar st *__iterState\n` +
      `\tif v, ok := __iterStates.Load(self); ok {\n\t\tst = v.(*__iterState)\n\t} else {\n` +
      `\t\tst = ${snap}\n\t\t__iterStates.Store(self, st)\n\t}\n` +
      `\tif st.pos >= len(st.keys) {\n\t\treturn ${a.option}_None{}\n\t}\n` +
      `${yieldStmt}\n}`;
    return iterFn + "\n\n" + nextFn;
  }

  // -- dynamic dispatch ---------------------------------------------------

  /** One `Trait_method` stub per dispatch table: it type-switches the `any`
   *  receiver — heap structs and containers by their `*Object` tag, value types by
   *  their Go struct, enums by their interface — and forwards to the concrete
   *  `Type_method`. Mirrors the AST backend's dispatch, but reads the resolved table
   *  off Core instead of re-deriving the trait's implementors. */
  private emitDispatchFn(d: CoreDispatch): string {
    const sym = `${d.trait}_${ident(d.method)}`;
    const params = ["tx *Txn", "self any", ...d.params.map((p, i) => `p${i} ${this.goType(p)}`)];
    const retStr = d.ret.k === "void" ? "" : " " + this.goType(d.ret);

    // Forward to a concrete method, asserting any Self-typed arg (an `any` stub
    // param) back to that impl's parameter type. `recv` is the type-switch binding.
    const forward = (c: CoreDispatch["cases"][number], recv: string): string => {
      const target = this.funcById.get(c.target);
      const args = d.params.map((p, i) => {
        const want = target?.params[i]?.type;
        return want && this.goType(want) !== this.goType(p) ? `p${i}.(${this.goType(want)})` : `p${i}`;
      });
      const call = `${c.target}(tx, ${recv}${args.length ? ", " + args.join(", ") : ""})`;
      return d.ret.k === "void" ? `${call}; return` : `return ${call}`;
    };

    // Heap structs and containers are `*Object`s → one tag switch; value types and
    // enums are distinct `any` cases.
    const tagCases: string[] = [];
    const otherCases: string[] = [];
    // A container iterates *as itself*: `Iterable.iter` reads the cursor-maker the
    // collection was built with (`collRec.iterFn`) rather than forwarding to a named
    // `Set_iter`, so no per-element function is dispatched to.
    const containerIter = d.trait === "Iterable" && d.method === "iter";
    for (const c of d.cases) {
      if (c.type.k === "generic") {
        const body = containerIter ? "return collNS(s).iterFn(tx, s)" : forward(c, "s");
        tagCases.push(`\t\tcase ${this.tag(CONTAINER_REC[c.type.name as "Set" | "Dict" | "List"])}:\n\t\t\t${body}`);
      }
      else if (c.type.k === "enum") otherCases.push(`\tcase ${c.type.name}:\n\t\t${forward(c, "s")}`);
      else if (c.type.k === "struct" && this.valueType.get(c.type.name)) otherCases.push(`\tcase ${c.type.name}:\n\t\t${forward(c, "s")}`);
      else if (c.type.k === "struct") tagCases.push(`\t\tcase ${this.tag(c.type.name)}:\n\t\t\t${forward(c, "s")}`);
    }

    const cases: string[] = [];
    if (tagCases.length) cases.push(`\tcase *Object:\n\t\tswitch s.tag {\n${tagCases.join("\n")}\n\t\t}`);
    cases.push(...otherCases);
    const panic = `\tpanic("${d.trait}.${d.method}: no impl for this type")\n}`;
    return (
      `func ${sym}(${params.join(", ")})${retStr} {\n` +
      `\tswitch s := self.(type) {\n${cases.join("\n")}\n\t}\n${panic}`
    );
  }

  private emitBlock(body: CoreStmt[], env: Env, depth: number): string {
    const inner = env.child();
    return body.map((s) => this.emitStmt(s, inner, depth)).join("");
  }

  private emitMatch(stmt: Extract<CoreStmt, { s: "match" }>, env: Env, depth: number): string {
    const pad = "\t".repeat(depth);
    const subj = this.emitExpr(stmt.subject, env);
    // A `_` arm becomes the type-switch `default`; the named arms become `case`s.
    // At most one wildcard is possible (the checker rejects arms after it).
    const wildcard = stmt.arms.find((a) => a.wildcard);
    const cases = stmt.arms
      .filter((a) => !a.wildcard)
      .map((arm) => {
        const armEnv = env.child();
        const binds = arm.bindings
          .map((b) => {
            // The enum is monomorphized, so the variant struct's payload field is
            // already the binding's concrete type — a plain read, no assertion.
            armEnv.declare(b.name, b.type);
            return `${pad}\t\t${ident(b.name)} := __m.${ident(b.field)}\n${pad}\t\t_ = ${ident(b.name)}\n`;
          })
          .join("");
        return `${pad}\tcase ${stmt.enumName}_${arm.variant}:\n${binds}${this.emitBlock(arm.body, armEnv, depth + 2)}`;
      });
    // With a `_` arm the default runs its body; without one, an unmatched variant
    // can't happen (the checker made the match exhaustive) — keep the panic as a
    // belt-and-suspenders guard.
    const dflt = wildcard
      ? `${pad}\tdefault:\n${pad}\t\t_ = __m\n${this.emitBlock(wildcard.body, env.child(), depth + 2)}`
      : `${pad}\tdefault:\n${pad}\t\t_ = __m\n${pad}\t\tpanic("no match arm")\n`;
    return `${pad}switch __m := (${subj}).(type) {\n` + cases.join("") + dflt + `${pad}}\n`;
  }

  // -- expressions --------------------------------------------------------

  private emitExpr(x: CoreExpr, env: Env): string {
    switch (x.e) {
      case "int": return `int64(${x.value})`;
      case "float": return goFloat(x.value);
      case "string": return JSON.stringify(x.value);
      case "char": return `rune(${x.value})`; // the code point; `rune` is int32
      case "bool": return String(x.value);
      case "local": return ident(x.name);
      case "field": {
        const obj = this.emitExpr(x.object, env);
        const ot = x.object.type;
        if (ot.k !== "struct") throw new GoGenError(`field access on ${ot.k} not lowered yet`);
        if (this.valueType.get(ot.name)) return `(${obj}).${ident(x.field)}`;
        // A heap struct is read through its record: in mvcc via the transactional
        // read (`get(tx)`); in-place directly (`rec`), as the 2PL lock is held.
        const rec = this.memory === "in-place" ? `${obj}.rec` : `${obj}.get(tx)`;
        return `(*${ot.name})(${rec}).${ident(x.field)}`;
      }
      case "unary":
        return x.op === "-" ? `(-${this.emitExpr(x.operand, env)})` : `(!${this.emitExpr(x.operand, env)})`;
      case "binop": {
        // Go has no ordering operators on `bool`, so lower `false < true` to the
        // equivalent boolean logic: a<b ≡ !a&&b, a>b ≡ a&&!b, a<=b ≡ !a||b,
        // a>=b ≡ a||!b. (`==`/`!=` on bool are native, handled by the fall-through.)
        if (x.left.type.k === "bool" && (x.op === "<" || x.op === ">" || x.op === "<=" || x.op === ">=")) {
          const l = this.emitExpr(x.left, env);
          const r = this.emitExpr(x.right, env);
          switch (x.op) {
            case "<": return `(!${l} && ${r})`;
            case ">": return `(${l} && !${r})`;
            case "<=": return `(!${l} || ${r})`;
            case ">=": return `(${l} || !${r})`;
          }
        }
        // Core binops are native; operands may need int->float widening. (String
        // and `char` ordering map straight to Go's native `<`/`>` on `string`/`rune`.)
        const t = x.type.k === "bool" ? binNumeric(x.left.type, x.right.type) : x.type;
        const l = this.coerce(this.emitExpr(x.left, env), x.left.type, t);
        const r = this.coerce(this.emitExpr(x.right, env), x.right.type, t);
        return `(${l} ${x.op} ${r})`;
      }
      case "if": {
        // Go's `if` is a statement, not an expression, so a value-producing
        // conditional is lowered to an immediately-invoked closure that returns the
        // taken branch. It captures the ambient `tx` lexically (always in scope where
        // an expression is emitted), so a branch that touches the store still works.
        const cond = this.emitExpr(x.cond, env);
        const then = this.coerce(this.emitExpr(x.then, env), x.then.type, x.type);
        const els = this.coerce(this.emitExpr(x.else, env), x.else.type, x.type);
        if (x.type.k === "void") {
          return `func() { if ${cond} { ${then} } else { ${els} } }()`;
        }
        return `func() ${this.goType(x.type)} { if ${cond} { return ${then} }; return ${els} }()`;
      }
      case "staticCall":
        return `${ident(x.target)}(tx${x.args.map((a) => ", " + this.emitExpr(a, env)).join("")})`;
      case "builtin":
        // Builtins (`print`, file IO, TCP net, …) are the backend's platform
        // surface; they live in `builtins.ts`, reached through a narrow context so
        // this file stays about lowering the language, not the host. The context
        // hands them just what they need: lowering an arg, scalar coercion, the Go
        // type of a monomorphized result, and a flag to pull in a host package.
        return emitBuiltin(x, {
          emit: (a) => this.emitExpr(a, env),
          coerce: (code, from, to) => this.coerce(code, from, to),
          goType: (t) => this.goType(t),
          use: (pkg) => {
            this.uses[pkg] = true;
          },
        });
      case "construct": {
        // Every field is present: the checker rejects an omitted field that has no
        // default, and a defaulted one is materialized as an argument upstream — so
        // there is no zero-value fill here (an empty collection, or anything else).
        const parts = x.fields.map((f) => `${ident(f.name)}: ${this.constructField(x.typeName, f.name, f.value, env)}`);
        const fields = parts.join(", ");
        return x.valueType
          ? `${x.typeName}{${fields}}`
          : `newObject(${this.tag(x.typeName)}, unsafe.Pointer(&${x.typeName}{${fields}}))`;
      }
      case "enumConstruct": {
        const fields = x.fields.map((f) => `${ident(f.name)}: ${this.emitExpr(f.value, env)}`).join(", ");
        return `${x.enumName}_${x.variant}{${fields}}`;
      }
      case "collectionNew": {
        // Set, Dict, and List share one runtime record (collRec); distinct tags let
        // display tell them apart.
        if (x.container !== "Set" && x.container !== "Dict" && x.container !== "List")
          throw new GoGenError(`collection \`${x.container}\` not lowered from Core yet`);
        const tag = CONTAINER_REC[x.container];
        // Install the element-typed cursor-maker so the collection is iterable
        // (`Iterable.iter`/`.iter()`) as itself — no coercion at the trait slot.
        return `newObject(${this.tag(tag)}, unsafe.Pointer(newCollRecIter(${x.iterFn})))`;
      }
      case "collectionLit": {
        if (x.container !== "List" && x.container !== "Dict" && x.container !== "Set")
          throw new GoGenError(`collection literal for \`${x.container}\` not lowered from Core yet`);
        // Keys, elements, and values are boxed into the store as `any`, so each is
        // coerced to its declared type first (an int in a `List<float>` widens here,
        // keeping its boxed dynamic type stable — the same rule `emitBuiltinMethod`
        // applies). A list/set passes its elements, a dict a flat key, value, … run.
        const parts = x.entries.flatMap((en) => [
          ...(en.key ? [this.coerce(this.emitExpr(en.key, env), en.key.type, x.elem)] : []),
          this.coerce(this.emitExpr(en.value, env), en.value.type, x.value ?? x.elem),
        ]);
        const build = { List: "__listLit", Set: "__setLit", Dict: "__dictLit" }[x.container];
        return `${build}(tx, ${[this.tag(CONTAINER_REC[x.container]), x.iterFn, ...parts].join(", ")})`;
      }
      case "builtinMethod":
        return this.emitBuiltinMethod(x, env);
      case "dynCall": {
        // Runtime dispatch: call the `Trait_method` stub (emitted from prog.dispatch),
        // which type-switches the receiver. The receiver and any Self-typed args box
        // to `any` at the boundary automatically.
        const args = [x.recv, ...x.args].map((a) => this.emitExpr(a, env)).join(", ");
        const call = `${x.trait}_${ident(x.method)}(tx, ${args})`;
        // A dispatch stub whose return depends on the trait's type parameters is
        // erased to `any` (each impl returns a different concrete type); here at the
        // call site we know the concrete type, so assert the result back to it.
        const disp = this.prog.dispatch.find((d) => d.trait === x.trait && d.method === x.method);
        const concrete = this.goType(x.type);
        return disp && this.goType(disp.ret) === "any" && concrete !== "any" ? `${call}.(${concrete})` : call;
      }
      case "funcRef":
        // A top-level CoreFunc used as a value: the Go func itself (its signature
        // already leads with `tx *Txn`, matching the fn-value calling convention).
        return ident(x.target);
      case "closure": {
        // A Go func literal; it closes over its captured locals lexically. The
        // leading `tx *Txn` keeps every function value uniformly callable. (Go
        // exempts function parameters from the unused-variable check, so no parking.)
        const inner = env.child();
        const params = ["tx *Txn", ...x.params.map((p) => {
          inner.declare(p.name, p.type);
          return `${ident(p.name)} ${this.goType(p.type)}`;
        })].join(", ");
        const retStr = x.ret.k === "void" ? "" : " " + this.goType(x.ret);
        // A closure is its own function boundary: its `return`s are the closure's, not
        // an enclosing txn's, and a `txn` inside it captures against the closure's ret.
        const prevRet = this.currentRet;
        const prevTxnRet = this.txnRet;
        this.currentRet = x.ret;
        this.txnRet = null;
        const body = this.emitBlock(x.body, inner, 2);
        this.currentRet = prevRet;
        this.txnRet = prevTxnRet;
        return `func(${params})${retStr} {\n${body}\t}`;
      }
      case "callValue": {
        // Calling a function value: pass the ambient `tx`, then the arguments, each
        // narrowed to the parameter's type — a value read as `any` (e.g. an erased
        // `Option.value`) needs an assertion to satisfy a concretely-typed parameter.
        const params = x.callee.type.k === "fn" ? x.callee.type.params : [];
        const args = x.args.map((a, i) => this.passArg(a, params[i], env));
        return `${this.emitExpr(x.callee, env)}(tx${args.map((a) => ", " + a).join("")})`;
      }
    }
  }

  /** An argument passed to a function value, narrowed to the parameter type: an
   *  `any`-typed value asserts to a concrete parameter type; otherwise the usual
   *  int→float widening applies. */
  private passArg(a: CoreExpr, param: Type | undefined, env: Env): string {
    const code = this.emitExpr(a, env);
    if (param && this.isConcrete(param) && (a.type.k === "unknown" || a.type.k === "trait")) {
      return `${code}.(${this.goType(param)})`;
    }
    return this.coerce(code, a.type, param ?? a.type);
  }

  /** A built-in method on a typed receiver → its runtime primitive, switching on the
   *  receiver's type. A Set/Dict/List op boxes keys/values to `any` in the store, so
   *  a key is coerced to the collection's element type first (keeping its boxed
   *  dynamic type stable, e.g. an int used as a float key) and a value read is
   *  asserted back to a concrete Go type; `Future.await` blocks and narrows the
   *  result. A user container-impl method (`impl<T> Trait for Set<T>`) isn't lowered
   *  here — it's a `staticCall`. */
  private emitBuiltinMethod(x: Extract<CoreExpr, { e: "builtinMethod" }>, env: Env): string {
    const recv = this.emitExpr(x.recv, env);
    const rt = x.recv.type;
    const container = rt.k === "generic" ? rt.name : "";
    const targs = rt.k === "generic" ? rt.args : [];
    const arg = (i: number, to?: Type): string => {
      const a = x.args[i]!;
      return to ? this.coerce(this.emitExpr(a, env), a.type, to) : this.emitExpr(a, env);
    };
    if (container === "Set") {
      const elem = targs[0];
      const key = () => arg(0, elem);
      switch (x.method) {
        case "add": return `__setAdd(tx, ${recv}, ${key()})`;
        case "remove": return `__setRemove(tx, ${recv}, ${key()})`;
        case "contains": return `__setContains(tx, ${recv}, ${key()})`;
        case "size": return `__collSize(tx, ${recv})`;
      }
    } else if (container === "Dict") {
      const [kt, vt] = targs;
      const key = () => arg(0, kt);
      switch (x.method) {
        case "set": return `__dictSet(tx, ${recv}, ${key()}, ${arg(1, vt)})`;
        case "get": {
          // `get` returns `Option<V>`: __dictGetOpt hands back (value, present), which
          // we wrap into the concrete monomorphized Option — Some(value) on a hit
          // (the boxed value narrowed back to V), None on a miss.
          const opt = this.goType(x.type);
          const payload = this.fromAny("__v", vt ?? UNKNOWN);
          return (
            `func() ${opt} {\n` +
            `\t\t__v, __ok := __dictGetOpt(tx, ${recv}, ${key()})\n` +
            `\t\tif !__ok {\n\t\t\treturn ${opt}_None{}\n\t\t}\n` +
            `\t\treturn ${opt}_Some{value: ${payload}}\n\t}()`
          );
        }
        case "has": case "contains": return `__dictHas(tx, ${recv}, ${key()})`;
        case "remove": return `__dictRemove(tx, ${recv}, ${key()})`;
        case "size": return `__collSize(tx, ${recv})`;
      }
    } else if (container === "List") {
      // A List is keyed by an `int` index; its element boxes to `any` in the store,
      // so `append`/`set` coerce the value to the element type and `get` asserts it
      // back to a concrete Go type. The index is already `int64` (stm `int`).
      const elem = targs[0];
      switch (x.method) {
        case "append": return `__listAppend(tx, ${recv}, ${arg(0, elem)})`;
        case "set": return `__listSet(tx, ${recv}, ${arg(0)}, ${arg(1, elem)})`;
        case "get": {
          const get = `__listGet(tx, ${recv}, ${arg(0)})`;
          return elem && this.isConcrete(elem) ? `${get}.(${this.goType(elem)})` : get;
        }
        case "size": return `__listSize(tx, ${recv})`;
      }
    }
    throw new GoGenError(`builtin method \`${container}.${x.method}\` not lowered from Core yet`);
  }

  /** Whether a value of this type asserts to a specific Go type (vs staying `any`). */
  private isConcrete(t: Type): boolean {
    return t.k !== "unknown" && t.k !== "trait";
  }

  /** Narrow an `any`-typed value (a collection key, a stored dict value) back to its
   *  element type: a trait element stays `any` (dispatched dynamically); an unknown
   *  is assumed a heap `*Object`; everything else asserts to its concrete Go type. */
  private fromAny(expr: string, t: Type): string {
    // A trait-typed slot stays `any` (the dispatch stub takes `any`). An *unknown*
    // element type arises in an ERASED generic container method — e.g. the single
    // `List_show(self)` emitted for `impl<T> Show for List<T>`, where `T` has no
    // binding: the element may be a boxed primitive (`int64`) OR an `*Object`, so we
    // can't assert either — keep it `any` and let `__show`/dispatch handle both. A
    // *concrete* element type asserts back to its Go type.
    if (t.k === "trait" || t.k === "unknown") return expr;
    return `${expr}.(${this.goType(t)})`;
  }

  /** A struct field initializer, widened to the declared field type. */
  private constructField(structName: string, field: string, value: CoreExpr, env: Env): string {
    const decl = this.prog.structs.find((s) => s.name === structName)!;
    const ft = decl.fields.find((f) => f.name === field)!.type;
    return this.coerce(this.emitExpr(value, env), value.type, ft);
  }

  // -- helpers ------------------------------------------------------------

  private coerce(code: string, from: Type, to: Type): string {
    return to.k === "float" && from.k === "int" ? `float64(${code})` : code;
  }

  private goType(t: Type): string {
    switch (t.k) {
      case "int": return "int64";
      case "float": return "float64";
      case "bool": return "bool";
      case "string": return "string";
      case "char": return "rune"; // a Unicode code point (int32)
      case "void": return "";
      case "unknown": return "any";
      case "trait": return "any";
      case "enum": case "enum-generic": return t.name;
      case "struct": case "struct-generic":
        return this.valueType.get(t.name) ? t.name : "*Object";
      case "generic": return "*Object";
      case "fn": {
        // A function value carries the ambient `tx` as its first parameter, matching
        // how every CoreFunc and closure is emitted (so a funcRef is the func itself).
        const params = ["*Txn", ...t.params.map((p) => this.goType(p))].join(", ");
        return t.ret.k === "void" ? `func(${params})` : `func(${params}) ${this.goType(t.ret)}`;
      }
      default:
        throw new GoGenError(`no Go type for ${t.k}`);
    }
  }

  private emitMain(): string {
    // In-place keeps no shared commit/abort counters (that atomic would be a
    // scaling bottleneck, and the count is statically known), so the footer omits
    // them in that mode — matching the runtime, which doesn't define them.
    const footer =
      this.memory === "in-place"
        ? `\tfmt.Fprintf(os.Stderr, "stm: %v (in-place locking)\\n", time.Since(__start))\n`
        : `\tfmt.Fprintf(os.Stderr, "stm: %v, %d commits, %d aborts, %d retries\\n", time.Since(__start), __commits.Load(), __aborts.Load(), __retries.Load())\n`;
    // A failed assertion is the program saying it did not do what it claims, so
    // the process says so too — after the footer, so the timing line is still
    // there to read on a failing run.
    const asserts = `\tif __assertFailed.Load() {\n\t\tos.Exit(1)\n\t}\n`;
    return `func main() {\n\t__start := time.Now()\n\tmain_stm(nil)\n${footer}${asserts}}`;
  }
}

class Env {
  private vars = new Map<string, Type>();
  constructor(private parent: Env | null = null) {}
  child(): Env {
    return new Env(this);
  }
  declare(name: string, t: Type) {
    this.vars.set(name, t);
  }
}

function goFloat(n: number): string {
  return Number.isInteger(n) ? `${n}.0` : String(n);
}

function binNumeric(l: Type, r: Type): Type {
  return l.k === "float" || r.k === "float" ? { k: "float" } : { k: "int" };
}

/** Does this `txn` body contain a `return` that leaves the enclosing function?
 *  Descends through ordinary control flow and *nested* `txn`s (a nested txn's
 *  return also has to propagate out through this one), but not into a `go` target
 *  (a separate function) or a closure (an expression — never a statement here). */
function stmtsHaveReturn(stmts: CoreStmt[]): boolean {
  return stmts.some(stmtHasReturn);
}
function stmtHasReturn(s: CoreStmt): boolean {
  switch (s.s) {
    case "return": return true;
    case "if": return stmtsHaveReturn(s.then) || (s.else ? stmtsHaveReturn(s.else) : false);
    case "while": return stmtsHaveReturn(s.body);
    case "for": return stmtsHaveReturn(s.body);
    case "match": return s.arms.some((a) => stmtsHaveReturn(a.body));
    case "txn": return stmtsHaveReturn(s.body);
    case "block": return stmtsHaveReturn(s.body);
    default: return false;
  }
}

/** Does every path through this body exit (via `return` or `retry`) — i.e. control
 *  can never fall off the end? When true, the code after `Run` is unreachable on
 *  fall-through, so the captured return propagates unconditionally (no flag). Mirrors
 *  Go's "terminating statement" notion; conservative on loops. */
function stmtsAlwaysExit(stmts: CoreStmt[]): boolean {
  const last = stmts[stmts.length - 1];
  return last ? stmtAlwaysExits(last) : false;
}
function stmtAlwaysExits(s: CoreStmt): boolean {
  switch (s.s) {
    case "return": return true;
    case "retry": return true;
    case "if": return s.else != null && stmtsAlwaysExit(s.then) && stmtsAlwaysExit(s.else);
    case "match": return s.arms.every((a) => stmtsAlwaysExit(a.body)); // exhaustive by construction
    case "txn": return stmtsAlwaysExit(s.body);
    case "block": return stmtsAlwaysExit(s.body);
    default: return false; // `while` (no break analysis), plain statements
  }
}
